import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { Box, Spinner } from '../../index';

interface ValidationIndicatorProps {
  isValidating?: boolean;
  isValid?: boolean;
  showSuccess?: boolean;
  size?: 'sm' | 'md' | 'lg';
  position?: 'inline' | 'absolute';
}

export function ValidationIndicator({
  isValidating = false,
  isValid = false,
  showSuccess = true,
  size = 'sm',
  position = 'inline',
}: ValidationIndicatorProps) {
  const sizeMap = {
    sm: { icon: 16, spinner: 'sm' },
    md: { icon: 20, spinner: 'md' },
    lg: { icon: 24, spinner: 'lg' },
  };

  const iconSize = sizeMap[size].icon;
  const spinnerSize = sizeMap[size].spinner as 'sm' | 'md' | 'lg';

  const containerStyle =
    position === 'absolute'
      ? {
          position: 'absolute' as const,
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
        }
      : {
          display: 'inline-flex',
          alignItems: 'center',
          marginLeft: '8px',
        };

  return (
    <Box style={containerStyle}>
      <AnimatePresence mode="wait">
        {isValidating && (
          <motion.div
            key="validating"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Spinner size={spinnerSize} color="#3B82F6" />
          </motion.div>
        )}

        {!isValidating && isValid && showSuccess && (
          <motion.div
            key="valid"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="#10B981">
              <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0 1A8 8 0 108 0a8 8 0 000 16z" />
              <path d="M10.97 4.97a.75.75 0 011.07 1.05l-3.99 4.99a.75.75 0 01-1.08.02L4.324 8.384a.75.75 0 111.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 01.02-.022z" />
            </svg>
          </motion.div>
        )}

        {!isValidating && !isValid && (
          <motion.div
            key="invalid"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" fill="#DC2626">
              <path d="M8 15A7 7 0 118 1a7 7 0 010 14zm0 1A8 8 0 108 0a8 8 0 000 16z" />
              <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
